import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import PageSEO from "@/components/seo/PageSEO";
import ConsumerFooter from "@/components/consumer/landing/ConsumerFooter";
import { useSecureAuth } from "@/hooks/useSecureAuth";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getRememberedEmail, saveRememberedEmail, clearRememberedEmail, getRememberMePreference, setRememberMePreference, clearAllAuthStorage } from "@/utils/authStorage";
import { isRestrictedBrowserContext } from "@/utils/browserDetection";

const ConsumerLoginPage = () => {
  const [email, setEmail] = useState(getRememberedEmail('seller')); // consumer uses 'seller' key pattern for now
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(getRememberMePreference('seller'));
  const [error, setError] = useState<string | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const { secureSignIn, isLoading } = useSecureAuth();
  const { session } = useAuth();
  const navigate = useNavigate();
  const alertRef = useRef<HTMLDivElement | null>(null);
  const [shake, setShake] = useState(false);

  // Scroll inline error into view + brief shake when an error appears
  useEffect(() => {
    if (error || rateLimitError) {
      alertRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      setShake(true);
      const t = setTimeout(() => setShake(false), 500);
      return () => clearTimeout(t);
    }
  }, [error, rateLimitError]);

  // Clean up stale auth data in restricted browser contexts
  useEffect(() => {
    const cleanupStaleAuth = async () => {
      if (isRestrictedBrowserContext()) {
        console.log('Restricted browser context detected, clearing stale auth data');
        clearAllAuthStorage();
        await supabase.auth.signOut().catch(() => {});
      }
    };
    cleanupStaleAuth();
  }, []);

  // Redirect if already logged in as consumer
  useEffect(() => {
    if (session) {
      supabase
        .from("consumer_profiles")
        .select("id")
        .eq("user_id", session.user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) navigate("/consumer/dashboard", { replace: true });
        });
    }
  }, [session, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);
    setRateLimitError(null);

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    try {
      const { error: signInError } = await secureSignIn(email, password, { silent: true });
      if (signInError) {
        if (signInError.message?.includes("Too many") || signInError.message?.includes("rate limit")) {
          setRateLimitError(signInError.message);
        } else {
          setError(signInError.message || "Invalid email or password. Please try again.");
        }
        return;
      }

      // Save or clear remembered email
      if (rememberMe) {
        saveRememberedEmail(email, 'seller');
        setRememberMePreference(true, 'seller');
      } else {
        clearRememberedEmail('seller');
        setRememberMePreference(false, 'seller');
      }

      navigate("/consumer/dashboard");
    } catch (err: any) {
      if (err.message?.includes("Too many")) {
        setRateLimitError(err.message);
      } else {
        setError(err.message || "Sign in failed. Please try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#080c14] text-white flex flex-col">
      <PageSEO
        title="Homeowner Login | GoDyspo"
        description="Log in to your GoDyspo homeowner account to track your property marketing progress."
        canonical="/consumer/login"
      />

      <header className="border-b border-border/30 bg-[#080c14]/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between h-16 px-4">
          <Link to="/consumer" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back</span>
          </Link>
          <Link to="/consumer" className="text-xl font-bold text-[#ffd700]">GoDyspo</Link>
          <div className="w-16" />
        </div>
      </header>

      <div className="flex-1 container mx-auto max-w-md px-4 pt-16 pb-12">
        <h1 className="text-3xl font-bold mb-2 text-center">Welcome Back</h1>
        <p className="text-slate-400 text-center mb-8">Log in to view your dashboard</p>

        <form onSubmit={handleSubmit} className={`space-y-4 ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`} noValidate>
          <div ref={alertRef}>
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {rateLimitError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{rateLimitError}</AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-white/5 border-white/10 text-white"
              placeholder="you@example.com"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/consumer/forgot-password"
                className="text-sm text-[#ffd700] hover:text-[#e6c200]"
              >
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-white/5 border-white/10 text-white"
              autoComplete="current-password"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(!!checked)}
            />
            <Label htmlFor="remember" className="text-sm font-normal">
              Remember me
            </Label>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] font-semibold"
            size="lg"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log In"}
          </Button>
        </form>

        <p className="text-center text-slate-400 mt-6 text-sm">
          Don't have an account?{" "}
          <Link to="/consumer/get-started" className="text-[#ffd700] hover:underline">
            Get Started
          </Link>
        </p>

      </div>

      <ConsumerFooter />
    </div>
  );
};

export default ConsumerLoginPage;
